import type { MessageId } from '@/shared/i18n/catalogue.en'
import type { AccountSession } from '@/shared/session/types'
import type { IconName } from '@/shared/ui/icons'

export const PROFILE_SECTIONS = ['personal', 'security', 'communications', 'ai', 'integrations', 'design-drafts'] as const
export type ProfileSection = (typeof PROFILE_SECTIONS)[number]

export const PROFILE_SECTION_META: Record<
  ProfileSection,
  { label: MessageId; description: MessageId; icon: IconName }
> = {
  personal: {
    label: 'profile.nav.personal',
    description: 'profile.nav.personalDescription',
    icon: 'identity-card',
  },
  security: { label: 'controlCenter.administration.security', description: 'controlCenter.administration.securityDescription', icon: 'shield-check' },
  communications: { label: 'controlCenter.administration.communications', description: 'controlCenter.administration.communicationsDescription', icon: 'chat' },
  ai: { label: 'profile.nav.settings', description: 'profile.nav.settingsDescription', icon: 'sparkle' },
  integrations: {
    label: 'externalAccess.title',
    description: 'externalAccess.subtitle',
    icon: 'link',
  },
  'design-drafts': { label: 'profile.nav.designDrafts', description: 'profile.nav.designDraftsDescription', icon: 'palette' },
}

/** The rail's titled blocks, in reading order, the way Control Center groups
 *  its sections. Every section stands in exactly one block. */
export const PROFILE_GROUPS: readonly { label: MessageId; sections: readonly ProfileSection[] }[] = [
  { label: 'profile.group.account', sections: ['personal', 'security', 'communications'] },
  { label: 'profile.group.connections', sections: ['ai', 'integrations'] },
  { label: 'profile.group.development', sections: ['design-drafts'] },
]

/** Design drafts are offered to the accounts the server grants design access,
 *  every other section to any signed-in account. Core authorizes each design
 *  read again; this only decides what the profile offers. */
export function profileSectionOffered(
  session: AccountSession | undefined,
  section: ProfileSection,
): boolean {
  if (session === undefined) return false
  return section === 'design-drafts' ? session.identity.designAccess : true
}

export function isProfileSection(value: unknown): value is ProfileSection {
  return (
    typeof value === 'string' &&
    (PROFILE_SECTIONS as readonly string[]).includes(value)
  )
}
